import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

// Add responsive styles
const styles = `
  @media (max-width: 768px) {
    .hero-title {
      font-size: 2rem !important;
    }
    .hero-description {
      font-size: 16px !important;
    }
  }
`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated, isLoading } = useAuth();
  
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: '',
    companyName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US'
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = ['Business Information', 'User Information'];

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/admin');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const validateEmail = (email) => {
    const regex = /\S+@\S+\.\S+/;
    return regex.test(email);
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      // Validate business information
      if (!formData.companyName.trim()) {
        newErrors.general = 'Company name is required';
      }

      if (!formData.phone.trim()) {
        newErrors.general = 'Phone number is required';
      }

      if (!formData.addressLine1.trim()) {
        newErrors.general = 'Address line 1 is required';
      }

      if (!formData.city.trim()) {
        newErrors.general = 'City is required';
      }

      if (!formData.state.trim()) {
        newErrors.general = 'State is required';
      }

      if (!formData.postalCode.trim()) {
        newErrors.general = 'Postal code is required';
      }
    } else if (step === 1) {
      // Validate user information
      if (!formData.firstName.trim()) {
        newErrors.general = 'First name is required';
      }

      if (!formData.lastName.trim()) {
        newErrors.general = 'Last name is required';
      }

      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!validateEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setErrors({});
  };

  const handleRegister = async () => {
    if (!validateStep(1)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        phone: formData.phone,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
        country: formData.country
      });
      
      toast.success('Registration successful! Redirecting to dashboard...');
      navigate('/admin');
    } catch (error) {
      console.log("Registration error:", error);
      
      if (error.response?.status === 400) {
        setErrors({ general: error.response.data.message });
      } else if (error.response?.status === 422) {
        const validationErrors = error.response.data.details || {};
        const errorMap = {};
        
        validationErrors.forEach((err) => {
          if (err.path && err.path[0]) {
            errorMap[err.path[0]] = err.message;
          }
        });
        
        setErrors(errorMap);
      } else {
        setErrors({ general: 'Registration failed. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Enter key for form submission
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleRegister();
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontFamily: 'Poppins, sans-serif'
      }}>
        <div>Checking authentication...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontFamily: 'Poppins, sans-serif'
      }}>
        <div>Redirecting to dashboard...</div>
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div style={{ 
        minHeight: '100vh',
        fontFamily: 'Inter, sans-serif',
        backgroundColor: '#FEFDF6',
        width: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}>
        {/* Hero Section */}
        <div className="hero-section" style={{
          position: 'relative',
          minHeight: '100vh',
          paddingTop: '40px',
          paddingBottom: '40px',
          overflow: 'hidden',
          background: 'linear-gradient(to bottom right, #74C0E6, #FEFDF6, rgba(189, 182, 253, 0.3))',
          width: '100%',
          boxSizing: 'border-box',
        }}>
        {/* Decorative background elements */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 0,
        }}>
          <div style={{
            position: 'absolute',
            top: '80px',
            left: '40px',
            width: '256px',
            height: '256px',
            backgroundColor: 'rgba(32, 150, 243, 0.2)',
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '80px',
            right: '40px',
            width: '384px',
            height: '384px',
            backgroundColor: 'rgba(108, 204, 22, 0.2)',
            borderRadius: '50%',
            filter: 'blur(60px)',
          }}></div>
        </div>

        {/* Hero Content */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 16px',
          textAlign: 'center',
          position: 'relative',
          zIndex: 10,
        }}>
          {/* Logo */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
          }}>
            <div className="logo-group" style={{
              position: 'relative',
              backgroundColor: '#6CCC16',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '8px',
              borderBottomLeftRadius: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer',
            }}>
              <div style={{
                overflow: 'hidden',
                transition: 'all 0.5s ease-in-out',
                maxWidth: '46px',
              }} className="logo-text-container">
                <span style={{
                  fontWeight: 900,
                  fontSize: '24px',
                  letterSpacing: '-0.5px',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}>IMObituary</span>
              </div>
              <div style={{
                position: 'relative',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible',
                width: '24px',
                height: '24px',
              }}>
                {/* Mail truck */}
                <div className="mail-truck" style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10,
                  opacity: 0,
                }}>
                  <i className="fa-solid fa-truck" style={{ fontSize: '12px', color: 'white' }}></i>
                </div>
                {/* Envelope icons */}
                <i className="fa-regular fa-envelope envelope-closed-animate" style={{
                  fontSize: '18px',
                  color: 'white',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 20,
                  opacity: 1,
                }}></i>
                <i className="fa-regular fa-envelope-open envelope-open-animate" style={{
                  fontSize: '18px',
                  color: 'white',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 20,
                  opacity: 0,
                }}></i>
                {/* Dollar signs */}
                <span className="dollar-sign-1" style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 30,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  opacity: 0,
                }}>$</span>
                <span className="dollar-sign-2" style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 30,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  opacity: 0,
                }}>$</span>
                <span className="dollar-sign-3" style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 30,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  opacity: 0,
                }}>$</span>
              </div>
              <div style={{
                position: 'absolute',
                bottom: '-8px',
                left: 0,
                width: 0,
                height: 0,
                borderTop: '8px solid #6CCC16',
                borderRight: '8px solid transparent',
              }}></div>
            </div>
          </div>

          <h1 className="hero-title" style={{
            fontSize: '3rem',
            fontWeight: 900,
            color: '#000000',
            marginBottom: '16px',
            letterSpacing: '-1px',
            textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
          }}>
            Join IMObituary
          </h1>
          <p className="hero-description" style={{
            fontSize: '20px',
            color: '#206884',
            maxWidth: '600px',
            margin: '0 auto 24px',
            fontWeight: 600,
          }}>
            Create a New Source of Revenue for Your Funeral Home. Get started with instant messaging obituaries today.
          </p>

          {/* BETA Notice */}
          <div style={{
            maxWidth: '500px',
            width: '100%',
            margin: '0 auto 24px',
            padding: '16px 20px',
            backgroundColor: 'rgba(44, 69, 117, 0.08)',
            border: '1px solid rgba(44, 69, 117, 0.25)',
            borderRadius: '12px',
            textAlign: 'left',
            boxSizing: 'border-box',
          }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: '#2c4575',
              color: 'white',
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              padding: '4px 10px',
              borderRadius: '6px',
              marginBottom: '10px',
            }}>
              BETA
            </div>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#2c4575',
              lineHeight: 1.5,
              fontWeight: 500,
            }}>
              We're in the early stages of IMObituary and actively improving the product. As an <strong>early adopter</strong>, you'll receive a <strong>90-day free trial</strong>.
            </p>
            <p style={{
              margin: '10px 0 0',
              fontSize: '14px',
              color: '#2c4575',
              lineHeight: 1.5,
            }}>
              Your feedback helps us build something that works for your funeral home. We'd love to hear from you as you use the platform.
            </p>
          </div>

          {/* Form Card */}
          <div style={{ 
            maxWidth: '500px',
            width: '100%',
            margin: '0 auto',
            boxSizing: 'border-box',
          }}>
            <div style={{ 
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '40px',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
              width: '100%',
              boxSizing: 'border-box',
              textAlign: 'left',
            }}>

          {/* Stepper */}
          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {steps.map((label, index) => (
                <div key={label} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: activeStep >= index ? '#2c4575' : '#e0e0e0',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '600',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      {index + 1}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: activeStep >= index ? '#2c4575' : '#999',
                      fontWeight: activeStep === index ? '600' : '400',
                      textAlign: 'center'
                    }}>
                      {label}
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div style={{
                      height: '2px',
                      flex: 1,
                      backgroundColor: activeStep > index ? '#2c4575' : '#e0e0e0',
                      margin: '0 8px',
                      marginBottom: '28px'
                    }} />
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <form onSubmit={(e) => { e.preventDefault(); activeStep === 1 ? handleRegister() : handleNext(); }} id="registerform">
            {/* Error Alert */}
            {errors.general && (
              <div style={{ 
                backgroundColor: '#f8d7da', 
                color: '#721c24', 
                padding: '12px', 
                borderRadius: '4px', 
                marginBottom: '20px',
                border: '1px solid #f5c6cb'
              }}>
                {errors.general}
              </div>
            )}

            {/* Step 0: Business Information */}
            {activeStep === 0 && (
              <>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Company Name
              </label>
              <input 
                type="text" 
                name="companyName" 
                placeholder="Enter your company name" 
                value={formData.companyName}
                onChange={handleInputChange('companyName')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Phone Number
              </label>
              <input 
                type="tel" 
                name="phone" 
                placeholder="Enter phone number" 
                value={formData.phone}
                onChange={handleInputChange('phone')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Address Line 1
              </label>
              <input 
                type="text" 
                name="addressLine1" 
                placeholder="Street address" 
                value={formData.addressLine1}
                onChange={handleInputChange('addressLine1')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Address Line 2 (Optional)
              </label>
              <input 
                type="text" 
                name="addressLine2" 
                placeholder="Apartment, suite, etc." 
                value={formData.addressLine2}
                onChange={handleInputChange('addressLine2')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                City
              </label>
              <input 
                type="text" 
                name="city" 
                placeholder="City" 
                value={formData.city}
                onChange={handleInputChange('city')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                State
              </label>
              <input 
                type="text" 
                name="state" 
                placeholder="State (e.g. CA)" 
                value={formData.state}
                onChange={handleInputChange('state')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Postal Code
              </label>
              <input 
                type="text" 
                name="postalCode" 
                placeholder="Postal code" 
                value={formData.postalCode}
                onChange={handleInputChange('postalCode')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Country
              </label>
              <select 
                name="country" 
                value={formData.country}
                onChange={handleInputChange('country')}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              >
                <option value="US">United States</option>
              </select>
            </div>
              </>
            )}

            {/* Step 1: User Information */}
            {activeStep === 1 && (
              <>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                First Name
              </label>
              <input 
                type="text" 
                name="firstName" 
                placeholder="Enter your first name" 
                value={formData.firstName}
                onChange={handleInputChange('firstName')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Last Name
              </label>
              <input 
                type="text" 
                name="lastName" 
                placeholder="Enter your last name" 
                value={formData.lastName}
                onChange={handleInputChange('lastName')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Email Address
              </label>
              <input 
                type="email" 
                name="email" 
                placeholder="Example@example.com" 
                value={formData.email}
                onChange={handleInputChange('email')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
              {errors.email && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {errors.email}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Password
              </label>
              <input 
                type="password" 
                name="password" 
                placeholder="Enter your password" 
                value={formData.password}
                onChange={handleInputChange('password')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
              {errors.password && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {errors.password}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#333', 
                fontWeight: '500',
                display: 'block',
                marginBottom: '8px',
                textAlign: 'left'
              }}>
                Confirm Password
              </label>
              <input 
                type="password" 
                name="confirmPassword" 
                placeholder="Confirm your password" 
                value={formData.confirmPassword}
                onChange={handleInputChange('confirmPassword')}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                style={{ 
                  border: '1px solid #ddd', 
                  padding: '10px 15px',
                  width: '100%',
                  borderRadius: '4px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }} 
                required 
              />
              {errors.confirmPassword && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {errors.confirmPassword}
                </div>
              )}
            </div>
              </>
            )}

            {/* Navigation Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              {activeStep > 0 && (
                <button
                  type="button"
                  onClick={handleBack}
                  disabled={isSubmitting}
                  style={{
                    flex: 1,
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#e5e7eb'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                >
                  Back
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  flex: 1,
                  backgroundColor: '#206884',
                  color: 'white',
                  border: 'none',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1,
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = '#1a5568';
                    e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    e.target.style.backgroundColor = '#206884';
                    e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                  }
                }}
              >
                {isSubmitting ? 'Creating Account...' : (activeStep === steps.length - 1 ? 'Register' : 'Next')}
              </button>
            </div>

            {/* Links */}
            <div style={{ 
              marginTop: '30px', 
              textAlign: 'center'
            }}>
              <a 
                href="/login" 
                style={{ 
                  color: '#206884', 
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
                onMouseEnter={(e) => {
                  e.target.style.textDecoration = 'underline';
                  e.target.style.color = '#1a5568';
                }}
                onMouseLeave={(e) => {
                  e.target.style.textDecoration = 'none';
                  e.target.style.color = '#206884';
                }}
              >
                Already have an account? Sign in
              </a>
            </div>
          </form>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default RegisterPage;
